import { forwardRef } from "react";
import styled from "@emotion/styled";
import type { SectionHeaderProps } from "./SectionHeader.types";

/* Exact compiled output of the pre-migration Tailwind utilities. Note:
 * tailwind-merge dropped the title's `leading-snug` because the size class
 * (text-sm/text-base) carries a font-size, so line-height comes from the
 * size class. The compact description's later `text-xs` likewise replaces
 * both text-sm and leading-5. */

const SectionHeaderRoot = styled("div", {
  label: "AgenticSectionHeader-root"
})({
  display: "flex",
  minWidth: "0px",
  alignItems: "flex-start",
  justifyContent: "space-between",
  gap: ".75rem"
});

const SectionHeaderText = styled("div", {
  label: "AgenticSectionHeader-text"
})({
  minWidth: "0px"
});

const SectionHeaderEyebrow = styled("div", {
  label: "AgenticSectionHeader-eyebrow"
})({
  marginBottom: ".25rem",
  fontSize: "10.5px",
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: ".08em",
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-text-muted) / var(--tw-text-opacity, 1))"
});

const SectionHeaderTitle = styled("div", {
  label: "AgenticSectionHeader-title",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { compact: boolean } }>(({ ownerState }) => [
  {
    fontWeight: 600,
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-text-primary) / var(--tw-text-opacity, 1))"
  },
  ownerState.compact
    ? { fontSize: ".875rem", lineHeight: "1.25rem" }
    : { fontSize: "1rem", lineHeight: "1.5rem" }
]);

const SectionHeaderDescription = styled("div", {
  label: "AgenticSectionHeader-description",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { compact: boolean } }>(({ ownerState }) => [
  {
    marginTop: ".25rem",
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-text-muted) / var(--tw-text-opacity, 1))"
  },
  ownerState.compact
    ? { fontSize: ".75rem", lineHeight: "1rem" }
    : { fontSize: ".875rem", lineHeight: "1.25rem" }
]);

const SectionHeaderActions = styled("div", {
  label: "AgenticSectionHeader-actions"
})({
  display: "flex",
  flexShrink: 0,
  alignItems: "center",
  gap: ".5rem"
});

export const SectionHeader = forwardRef<HTMLDivElement, SectionHeaderProps>(
  ({ className, title, description, eyebrow, actions, compact, titleClassName, descriptionClassName, ...props }, ref) => (
    <SectionHeaderRoot ref={ref} className={className} {...props}>
      <SectionHeaderText>
        {eyebrow && <SectionHeaderEyebrow>{eyebrow}</SectionHeaderEyebrow>}
        <SectionHeaderTitle ownerState={{ compact: Boolean(compact) }} className={titleClassName}>{title}</SectionHeaderTitle>
        {description && (
          <SectionHeaderDescription ownerState={{ compact: Boolean(compact) }} className={descriptionClassName}>
            {description}
          </SectionHeaderDescription>
        )}
      </SectionHeaderText>
      {actions && <SectionHeaderActions>{actions}</SectionHeaderActions>}
    </SectionHeaderRoot>
  )
);

SectionHeader.displayName = "SectionHeader";
